// ProfilePhoto.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useOnline } from 'context/OnlineContext';
import { useWorkspace } from 'context/WorkspaceContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Utilities
import { PhotoShape } from 'utilities/Enums';
import { buildWorkspaceMemberProfilePath } from 'utilities/Routing';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// CSS
import './ProfilePhoto.css';

/**
 * ProfilePhoto
 *
 * This component renders an icon or letter representing a user.
 *
 * @param {object} node - The USER node to represent.
 * @param {number} size - The size of the photo in pixels. Default is 40.
 * @param {number} borderWidth - The width of the border in pixels. Default is 2.
 * @param {string} borderColor - Optional border color override.
 * @param {string|number} borderRadius - The border radius. Default is 7px.
 * @param {boolean} allowNavigation - Whether clicking should navigate to the member profile route.
 * @param {function} onClick - Click handler for the photo.
 * @param {string} shape - The shape type for the photo.
 *
 * @returns {ReactNode} The rendered component.
 */
const ProfilePhoto = React.memo(({
    node,
    size = 40,
    borderWidth = 2,
    borderColor,
    borderRadius = '7px',
    allowNavigation = false,
    onClick,
    shape = PhotoShape.CIRCLE
}) => {

    // ----- CONTEXT ----- //

    // Online Context
    const { isUserOnline } = useOnline();

    // Theme Context
    const { theme } = useTheme();

    // Workspace Context
    const { activeWorkspace } = useWorkspace();

    // Navigation
    const navigate = useNavigate();

    // ----- STATE ----- //

    // Resolved Cover URL State
    const [resolvedCoverUrl, setResolvedCoverUrl] = useState(null);

    // Image Failure State
    const [imageFailed, setImageFailed] = useState(false);

    // ----- MEMOS ----- //

    // Memoized user key used for online state lookup.
    const userKey = useMemo(() => {
        return String(node?.userKey || node?.key || '').trim();
    }, [node?.key, node?.userKey]);

    const workspaceKey = useMemo(() => {
        return String(node?.workspaceKey || activeWorkspace?.key || '').trim();
    }, [activeWorkspace?.key, node?.workspaceKey]);

    const canNavigateToProfile = Boolean(allowNavigation && userKey && workspaceKey);

    // ----- UTILITIES ----- //

    const rawCoverUrl = typeof node?.coverUrl === 'string' ? node.coverUrl.trim() : '';
    const canRenderRawCoverUrl = (
        rawCoverUrl.startsWith('blob:')
        || rawCoverUrl.startsWith('data:')
        || rawCoverUrl.includes('://')
    );

    const showOnlineBorder = Boolean(userKey) && isUserOnline(userKey);
    const effectiveBorderWidth = showOnlineBorder ? 1 : borderWidth;
    const effectiveBorderColor = showOnlineBorder ? theme.active : borderColor;
    const radius = shape === PhotoShape.CIRCLE ? '50%' : borderRadius;
    const imageSrc = resolvedCoverUrl || (canRenderRawCoverUrl ? rawCoverUrl : null);
    const showImage = Boolean(imageSrc) && !imageFailed;

    // ----- EFFECTS ----- //

    // Resolves an image URL from direct URL values or storage paths.
    useEffect(() => {
        let cancelled = false;

        const raw = rawCoverUrl;
        const coverStoragePath = typeof node?.coverStoragePath === 'string'
            ? node.coverStoragePath.trim()
            : (typeof node?.storagePath === 'string' ? node.storagePath.trim() : '');

        setImageFailed(false);

        if (!raw && !coverStoragePath) {
            setResolvedCoverUrl(null);
            return undefined;
        }

        if (raw.startsWith('blob:') || raw.startsWith('data:')) {
            setResolvedCoverUrl(raw);
            return undefined;
        }

        const nodeKey = node?.key || node?.userKey || null;

        (async () => {
            try {
                const url = await resolveImageUrl({
                    nodeKey,
                    url: raw || null,
                    storagePath: coverStoragePath || null,
                });
                if (!cancelled) {
                    setResolvedCoverUrl(url || (canRenderRawCoverUrl ? raw : null));
                }
            } catch (e) {
                if (!cancelled) {
                    setResolvedCoverUrl(canRenderRawCoverUrl ? raw : null);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [canRenderRawCoverUrl, node?.coverStoragePath, node?.key, node?.storagePath, node?.userKey, rawCoverUrl]);

    // Resets image failure state when a new image source is resolved.
    useEffect(() => {
        if (resolvedCoverUrl) {
            setImageFailed(false);
        }
    }, [resolvedCoverUrl]);

    // ----- HANDLERS ----- //

    /**
     * Handles image load failures and falls back to the initial avatar.
     */
    const handleImageError = () => {
        setImageFailed(true);
    };

    const handleClick = (event) => {
        if (typeof onClick === 'function') {
            onClick(event);
        }

        if (event?.defaultPrevented) {
            return;
        }

        if (!canNavigateToProfile) {
            return;
        }

        navigate(buildWorkspaceMemberProfilePath(workspaceKey, userKey));
    };

    const handleKeyDown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        handleClick(event);
    };

    // ----- RENDER ----- //

    if (!node) return null;

    return (
        <div
            className="profile-photo-container"
            style={{
                backgroundColor: theme.backgroundMuted,
                borderColor: effectiveBorderColor || undefined,
                borderRadius: radius,
                borderWidth: `${effectiveBorderWidth}px`,
                cursor: canNavigateToProfile || typeof onClick === 'function' ? 'pointer' : undefined,
                height: `${size}px`,
                width: `${size}px`,
            }}
            role={canNavigateToProfile || typeof onClick === 'function' ? 'button' : undefined}
            tabIndex={canNavigateToProfile || typeof onClick === 'function' ? 0 : undefined}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            {showImage ? (
                <>
                    {/* IMAGE */}
                    <img
                        className="profile-photo-image"
                        style={{
                            borderRadius: radius,
                        }}
                        alt={node.title}
                        src={imageSrc}
                        onError={handleImageError}
                    />
                </>
            ) : (
                <>
                    {/* INITIAL */}
                    <div
                        className="profile-photo-initial"
                        style={{
                            '--dot-font-size': size * 0.7 + 'px',
                            '--dot-size': size + 'px',
                            color: theme.foreground,
                        }}
                    >
                        {(node?.name || node?.user?.name)?.charAt(0) || '?'}
                    </div>
                </>
            )}
        </div>
    );
});

// Dev Tools
ProfilePhoto.displayName = 'ProfilePhoto';

export default ProfilePhoto;
